import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authoritiesAPI } from '../../api/client';
import { formatPhoneInput } from '../../lib/validators';
import { AGENCY_TYPES, JURISDICTION_LEVELS, RANKS } from '../../lib/emergencyConstants';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';

function SectionTitle({ n, children }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="w-6 h-6 bg-soem-primary text-white rounded-full flex items-center justify-center text-xs font-bold">{n}</div>
      <h3 className="font-bold text-soem-navy">{children}</h3>
      <div className="flex-1 h-px bg-slate-200" />
    </div>
  );
}

export default function AuthorityForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    dependency_name: 'proteccion_civil',
    jurisdiction_level: 'estatal',
    titular_name: '',
    rank: 'comisionado',
    direct_phone: '',
    institutional_email: '',
    notes: '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary";

  useEffect(() => {
    if (isEdit) {
      authoritiesAPI.get(id).then(r => {
        const a = r.data;
        setForm({
          dependency_name: a.dependency_name || 'proteccion_civil',
          jurisdiction_level: a.jurisdiction_level || 'estatal',
          titular_name: a.titular_name || '',
          rank: a.rank || 'comisionado',
          direct_phone: a.direct_phone || '',
          institutional_email: a.institutional_email || '',
          notes: a.notes || '',
        });
      }).catch(() => toast.error('Error al cargar'));
    }
  }, [id, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.titular_name) { toast.error('El nombre del titular es requerido'); return; }
    setSaving(true);
    try {
      const payload = {
        dependency_name: form.dependency_name,
        jurisdiction_level: form.jurisdiction_level,
        titular_name: form.titular_name,
        rank: form.rank,
        direct_phone: form.direct_phone || null,
        institutional_email: form.institutional_email || null,
        notes: form.notes || null,
      };
      if (isEdit) {
        await authoritiesAPI.update(id, payload);
        toast.success('Autoridad actualizada');
        navigate(`/dashboard/authorities/${id}`);
      } else {
        const r = await authoritiesAPI.create(payload);
        toast.success('Autoridad creada');
        navigate(`/dashboard/authorities/${r.data.id}`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : JSON.stringify(msg).slice(0, 80));
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8 animate-fade-in" data-testid="authority-form">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/dashboard/authorities')} className="soem-btn-secondary p-2" aria-label="Volver a autoridades"><ArrowLeft size={18} /></button>
        <h1 className="text-2xl font-bold text-soem-navy">{isEdit ? 'Editar Autoridad' : 'Nueva Autoridad Vinculada'}</h1>
      </div>
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* S1: Dependencia */}
        <div className="soem-card p-6 space-y-5">
          <SectionTitle n="1">Dependencia</SectionTitle>
          <div>
            <label htmlFor="f-tipo-de-dependencia" className="soem-label block mb-2">Tipo de dependencia</label>
            <div className="grid grid-cols-3 gap-3">
              {Object.entries(AGENCY_TYPES).map(([k, v]) => (
                <button key={k} type="button" onClick={() => set('dependency_name', k)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-sm border-2 font-semibold text-sm transition-all ${form.dependency_name === k ? `${v.activeBg} text-white border-transparent` : `border-slate-200 ${v.text} hover:border-slate-300 ${v.bg}`}`}>
                  <span className="text-3xl">{v.emoji}</span>
                  <span className="text-center leading-tight text-xs">{v.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div>
            <label htmlFor="f-nivel-de-jurisdicción" className="soem-label block mb-1.5">Nivel de jurisdicción</label>
            <select id="f-nivel-de-jurisdicción" className={inputCls} value={form.jurisdiction_level} onChange={e => set('jurisdiction_level', e.target.value)}>
              {Object.entries(JURISDICTION_LEVELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            {form.jurisdiction_level && (
              <p className="text-xs text-slate-400 mt-1">{JURISDICTION_LEVELS[form.jurisdiction_level]?.desc}</p>
            )}
          </div>
        </div>

        {/* S2: Titular */}
        <div className="soem-card p-6 space-y-4">
          <SectionTitle n="2">Titular</SectionTitle>
          <div>
            <label htmlFor="f-nombre-del-titular" className="soem-label block mb-1.5">Nombre del titular <span className="text-red-500">*</span></label>
            <input id="f-nombre-del-titular" className={inputCls} value={form.titular_name} onChange={e => set('titular_name', e.target.value)} required data-testid="titular-name" />
          </div>
          <div>
            <label htmlFor="f-grado-rango" className="soem-label block mb-2">Grado / Rango</label>
            <div className="flex gap-3">
              {Object.entries(RANKS).map(([k, v]) => (
                <button key={k} type="button" onClick={() => set('rank', k)}
                  className={`flex-1 py-3 rounded-sm border-2 font-bold text-sm transition-all ${form.rank === k ? 'bg-soem-navy text-white border-soem-navy' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                  {v.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* S3: Contacto */}
        <div className="soem-card p-6 space-y-4">
          <SectionTitle n="3">Contacto</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="f-teléfono-directo" className="soem-label block mb-1.5">Teléfono directo</label>
              <input id="f-teléfono-directo" 
                type="tel" 
                className={inputCls} 
                value={form.direct_phone} 
                onChange={e => set('direct_phone', formatPhoneInput(e.target.value))} 
                placeholder="10 dígitos" 
                maxLength={10}
              />
            </div>
            <div>
              <label htmlFor="f-correo-institucional" className="soem-label block mb-1.5">Correo institucional</label>
              <input id="f-correo-institucional" type="email" className={inputCls} value={form.institutional_email} onChange={e => set('institutional_email', e.target.value)} placeholder="contacto@gob.mx" />
            </div>
          </div>
        </div>

        {/* S4: Notas */}
        <div className="soem-card p-6">
          <SectionTitle n="4">Notas</SectionTitle>
          <textarea className={`${inputCls} resize-none`} rows={3} value={form.notes}
            onChange={e => set('notes', e.target.value)} placeholder="Información adicional..." />
        </div>

        <div className="flex gap-4">
          <button type="submit" disabled={saving}
            className="soem-btn-primary flex-1 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
            data-testid="save-auth-btn">
            <Save size={15} /> {saving ? 'Guardando...' : (isEdit ? 'Actualizar' : 'Guardar Autoridad')}
          </button>
          <button type="button" onClick={() => navigate('/dashboard/authorities')}
            className="soem-btn-secondary px-8 py-3 text-sm">Cancelar</button>
        </div>
      </form>
    </div>
  );
}
